/**
 * Sprecher-Namensräume (Doc 13 §2.2) und deterministische Farben (Doc 05 §5).
 *
 * | Quelle                      | speaker.id            |
 * |-----------------------------|-----------------------|
 * | lapel, remote (1 Sprecher)  | `src:<sourceId>`      |
 * | room mit Diarization        | `spk_<n>@<sourceId>`  |
 * | room ohne Diarization       | `room:<sourceId>`     |
 * | room, Stimme erkannt (S3b)  | `vid:<slug>@<sourceId>` |
 *
 * `vid:` (Doc 13 §4.2b): die Engine hat einen bekannten Sprecher per
 * Stimm-Identifikation erkannt und liefert `id:<label>`; `slug` ist das Label
 * in Kleinbuchstaben/Ziffern/`-` (Umlaute transkribiert), das Label selbst
 * steht in der Registry.
 */
import type { Processing, SourceConfig, SourceKind } from './common.js';

export type SpeakerNamespace = 'src' | 'spk' | 'room' | 'vid';

export interface ParsedSpeakerId {
  ns: SpeakerNamespace;
  sourceId: string;
  /** nur bei `spk`: Index n aus `spk_<n>` */
  index?: number;
  /** nur bei `vid`: Slug des erkannten Labels */
  slug?: string;
}

/** Präfix, mit dem Engines erkannte (identifizierte) Sprecher melden: `id:<label>`. */
export const IDENTIFIED_ENGINE_PREFIX = 'id:';

/** Engine-Label eines erkannten Sprechers (`id:<label>`). */
export function identifiedEngineLabel(label: string): string {
  return `${IDENTIFIED_ENGINE_PREFIX}${label}`;
}

/** `id:<label>` → `<label>`, sonst null. */
export function identifiedLabelOf(engineSpeaker: string | undefined | null): string | null {
  if (typeof engineSpeaker !== 'string' || !engineSpeaker.startsWith(IDENTIFIED_ENGINE_PREFIX)) return null;
  const label = engineSpeaker.slice(IDENTIFIED_ENGINE_PREFIX.length).trim();
  return label || null;
}

const TRANSLIT: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', æ: 'ae', ø: 'oe', å: 'aa', œ: 'oe', ł: 'l', đ: 'd', þ: 'th' };

/**
 * Label → Slug: Kleinbuchstaben, Ziffern und `-` (Umlaute transkribiert,
 * Akzente entfernt, alles andere wird `-`, keine doppelten/Rand-Striche).
 * Leeres Ergebnis (z. B. nur Satzzeichen oder nicht-lateinische Schrift) →
 * `v` + FNV-Hash, damit verschiedene Labels verschiedene IDs bekommen.
 */
export function speakerSlug(label: string): string {
  const lower = label.trim().toLocaleLowerCase('de');
  let out = '';
  for (const ch of lower) out += TRANSLIT[ch] ?? ch;
  out = out
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
  return out || `v${fnv1a(lower).toString(36)}`;
}

/** Erkannter Sprecher `label` in Quelle `sourceId` → `vid:<slug>@<sourceId>`. */
export function identifiedSpeakerId(label: string, sourceId: string): string {
  return `vid:${speakerSlug(label)}@${sourceId}`;
}

export function lapelSpeakerId(sourceId: string): string {
  return `src:${sourceId}`;
}

export function roomSpeakerId(sourceId: string): string {
  return `room:${sourceId}`;
}

/** Engine-Label (`spk_0`, `S1`, `2`, …) → `spk_<n>@<sourceId>`. */
export function diarizedSpeakerId(engineLabel: string | number, sourceId: string): string {
  let n: number;
  if (typeof engineLabel === 'number') {
    n = engineLabel;
  } else {
    const m = /(\d+)\s*$/.exec(engineLabel);
    n = m ? Number(m[1]) : stableIndex(engineLabel);
  }
  return `spk_${n}@${sourceId}`;
}

/** Diarization-Default nach Doc 13 §2.1: kind=room → true. */
export function defaultDiarize(kind: SourceKind): boolean {
  return kind === 'room';
}

/** Verarbeitungs-Default pro Art (Doc 12 §2.2, Doc 13 §3.1): Lapel an, Raum aus. */
export function defaultProcessing(kind: SourceKind): Processing {
  const on = kind !== 'room';
  return { ec: on, ns: on, agc: on };
}

export function effectiveDiarize(source: Pick<SourceConfig, 'kind' | 'diarize'>): boolean {
  if (source.kind !== 'room') return false; // Ein-Sprecher-Quellen werden nie diarisiert
  return source.diarize ?? defaultDiarize(source.kind);
}

/** Sprecher-ID für ein Engine-Segment dieser Quelle. */
export function speakerIdFor(
  source: Pick<SourceConfig, 'id' | 'kind' | 'diarize'>,
  engineSpeaker?: string,
): string {
  if (source.kind === 'lapel' || source.kind === 'remote') return lapelSpeakerId(source.id);
  if (effectiveDiarize(source)) {
    const label = identifiedLabelOf(engineSpeaker);
    if (label) return identifiedSpeakerId(label, source.id);
    return diarizedSpeakerId(engineSpeaker ?? 'spk_0', source.id);
  }
  return roomSpeakerId(source.id);
}

export function parseSpeakerId(id: string): ParsedSpeakerId | null {
  if (id.startsWith('src:')) return id.length > 4 ? { ns: 'src', sourceId: id.slice(4) } : null;
  if (id.startsWith('room:')) return id.length > 5 ? { ns: 'room', sourceId: id.slice(5) } : null;
  const m = /^spk_(\d+)@(.+)$/.exec(id);
  if (m) return { ns: 'spk', index: Number(m[1]), sourceId: m[2] as string };
  const v = /^vid:([a-z0-9-]+)@(.+)$/.exec(id);
  if (v) return { ns: 'vid', slug: v[1] as string, sourceId: v[2] as string };
  return null;
}

/** Acht kontrastreiche Farben (auf hellem und dunklem Grund lesbar). */
export const SPEAKER_PALETTE = [
  '#e4572e',
  '#2e86ab',
  '#76b041',
  '#a23b72',
  '#f18f01',
  '#3b8b8b',
  '#6c5ce7',
  '#c0392b',
] as const;

/** FNV-1a 32 bit — deterministisch, plattformunabhängig. */
export function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function speakerColor(speakerId: string): string {
  return SPEAKER_PALETTE[fnv1a(speakerId) % SPEAKER_PALETTE.length] as string;
}

function stableIndex(label: string): number {
  return fnv1a(label) % 1000;
}
